import { INSTRUCTION_LRS } from 'constants/instructions';
import {
  BILL_PAY_PAYMENT_METHOD,
  BPAY_PAYMENT_METHOD,
  EMAIL_PAYMENT_METHOD,
  InstructionsType,
  LULU_EMAIL,
  PAYNOW_PAYMENT_OPTIONS,
  QR_PAYMENT_METHOD,
} from 'constants/offers';
import get from 'lodash/get';
import type { Field } from 'models/field';
import type { Offer, RecurringOffer } from 'models/offer';
import type { Price } from 'models/price';
import type { RootState } from 'reducers/types';
import { getSelectedOffer, getSelectedOfferId } from 'selectors/ui/ui';
import { isStablecoin } from 'utils/currency/currency';
import { getLocalCurrency } from '../currencies/currencies';
import { getFieldById } from '../fields/fields';
import { getInstructionByName } from '../instructions/instructions';
import { getOrderOffer } from '../order/order';
import {
  getPaymentInstruction,
  getPaymentOffer,
  getPaymentOfferFromPayment,
} from '../payment/payment';

export const ASTRO_PAY_BOLETO = 'astropay_br_boleto';

const NO_FIELDS: Field[] = [];

const getFee = (offer: Offer, price: Price) => {
  const { currency } = price;
  const { fees: value } = offer;

  return { currency, value };
};

export const getOffer = (state: RootState, id: string) => {
  const offer = findOffer(state, id);

  if (!offer) {
    return {} as Offer;
  }

  return buildOfferWithEntities(state, offer);
};

export const getOfferName = (state: RootState, id: string) => {
  const {
    entities: { offers: { byId: offersById } = {} },
  } = state;
  const offer = offersById?.[id];

  return offer ? offer.name : '';
};

export const isRecurring = (offer: Offer) => {
  return !!offer.recurring;
};

export const getOffers = (state: RootState): Offer[] => {
  const {
    entities: {
      offers: { ids },
    },
  } = state;

  return ids.map(id => getOffer(state, id));
};

export const getLocalOffers = (state: RootState) => {
  const offers = getSingleOffers(state);
  const { code: localCurrency } = getLocalCurrency(state);

  return offers.filter(({ price }) => {
    return (
      (price as Price).currency === localCurrency ||
      isStablecoin(price.currency)
    );
  });
};

export const getForeignOffers = (state: RootState) => {
  const offers = getSingleOffers(state);
  const { code: localCurrency } = getLocalCurrency(state);

  return offers.filter(({ price }) => {
    return (
      (price as Price).currency !== localCurrency &&
      !isStablecoin(price.currency)
    );
  });
};

export const getSingleOffers = (state: RootState) => {
  const {
    entities: { offers: { ids } = {} },
  } = state;

  return ids?.map(id => getSingleOffer(state, id)) || [];
};

export const getRecurringOffers = (state: RootState) => {
  const {
    entities: { recurringOffers: { ids } = {} },
  } = state;

  return ids?.map(id => getRecurringOffer(state, id)) || [];
};

const getSingleOffer = (state: RootState, id: string) => {
  const {
    entities: {
      offers: { byId },
    },
  } = state;

  return buildOfferWithEntities(state, byId[id]);
};

export const getRecurringOffer = (state: RootState, id: string) => {
  const {
    entities: {
      recurringOffers: { byId },
    },
  } = state;

  return buildOfferWithEntities(state, byId[id]);
};

export const getSelectedRecurringOffer = (state: RootState): RecurringOffer => {
  const id = getSelectedOfferId(state);

  return getRecurringOffer(state, id);
};

export const isNonAutomatedRecurring = (state: RootState) => {
  const id = getSelectedOfferId(state);

  if (!id) {
    return false;
  }

  const recurringOffer = findRecurringOffer(state, id);

  if (!recurringOffer) {
    return false;
  }

  return !recurringOffer.recurring;
};

export const isLulu = (state: RootState) => {
  const { value = '' } = getPaymentInstruction(state, INSTRUCTION_LRS) || {};
  return value.includes(LULU_EMAIL);
};

const INDIAN_PARTNERS = ['lulu', 'muthoot', 'cashfree'];
const INDIAN_PARTNERS_DATA: Record<string, { name: string; gstin: string }> = {
  lulu: {
    name: 'Lulu Forex',
    gstin: '32AABCL8839J1Z9',
  },
  muthoot: {
    name: 'Muthoot Forex',
    gstin: '32AADCM3244L1ZL',
  },
  cashfree: {
    name: 'Muthoot Forex',
    gstin: '32AADCM3244L1ZL',
  },
};

const NO_INDIAN_PARTNER_DATA = {};

export const indianPartnerData = (state: RootState) => {
  const { paymentOption } = getOrderOffer(state);

  if (!paymentOption) {
    return NO_INDIAN_PARTNER_DATA;
  }

  const paymentOptionLower = paymentOption.toLowerCase();
  const partner = INDIAN_PARTNERS.find(partner =>
    paymentOptionLower.includes(partner),
  );

  if (partner) {
    return INDIAN_PARTNERS_DATA[partner];
  }

  return NO_INDIAN_PARTNER_DATA;
};

export const isLuluOfferSelected = (state: RootState) => {
  const { paymentOption } = getOrderOffer(state);

  if (!paymentOption) {
    return false;
  }

  return paymentOption.toUpperCase().includes('LULU');
};

export const isOppOfferSelected = (state: RootState) => {
  const paymentOffer = getPaymentOffer(state);
  return /opp/i.test(paymentOffer.paymentOption);
};

export const isDirectDebitOfferSelected = (state: RootState) => {
  const offer = getSelectedOffer(state);

  return offer?.paymentMethod === 'direct_debit';
};

export const getRepeatPaymentOffer = (state: RootState) => {
  return get(state, 'entities.repeatPaymentOffer');
};

export const shouldShowEURateDisclaimer = (state: RootState) => {
  const {
    entities: {
      offers: { byId: offers, ids },
    },
  } = state;

  const hasMarkupRate = (id: string) => !!offers[id].markupRate;
  return ids.some(hasMarkupRate);
};

export function forceRedirectInNewWindow(state: RootState) {
  const offer = getSelectedOffer(state);
  const redirectUrl = getInstructionByName(state, 'redirect_url') || {};
  const instructionsTypes =
    getInstructionByName(state, 'type')?.value.split(',') || [];

  // biome-ignore lint/suspicious/noPrototypeBuiltins: no explanation
  if (!redirectUrl?.hasOwnProperty('value')) {
    return false;
  }

  if (!instructionsTypes.includes('embedded')) {
    return true;
  }

  return (
    ([ASTRO_PAY_BOLETO].includes(offer?.paymentOption) &&
      offer?.paymentMethod === 'bank_transfer') ||
    (offer?.paymentOption === 'dlocal_cop_pse' &&
      offer?.paymentMethod === 'online')
  );
}

export function getInstructionsType(state: RootState) {
  const { paymentMethodInternalName, paymentOption } =
    getPaymentOfferFromPayment(state);
  const redirectURL = getInstructionByName(state, 'redirect_url');
  const embeddedScript = getInstructionByName(state, 'embedded_script');

  if (QR_PAYMENT_METHOD.includes(paymentMethodInternalName)) {
    return InstructionsType.QR;
  }
  if (EMAIL_PAYMENT_METHOD.includes(paymentMethodInternalName)) {
    return InstructionsType.EMAIL;
  }
  if (BPAY_PAYMENT_METHOD.includes(paymentMethodInternalName)) {
    return InstructionsType.BPAY;
  }

  if (BILL_PAY_PAYMENT_METHOD.includes(paymentMethodInternalName)) {
    return InstructionsType.BILLPAY;
  }

  if (redirectURL && !embeddedScript) {
    return InstructionsType.REDIRECT;
  }
  if (PAYNOW_PAYMENT_OPTIONS.includes(paymentOption)) {
    return InstructionsType.PAYNOW;
  }

  return InstructionsType.EMPTY;
}

export const isBoleto = (state: RootState) => {
  const offer = getSelectedOffer(state);
  const boletoPaymentOptions = [ASTRO_PAY_BOLETO];
  return boletoPaymentOptions.includes(offer?.paymentOption);
};

export const getRecurringOffersMetadata = (state: RootState) => {
  return state.entities.recurringOffers.metadata;
};

export const getRecurringOffersConfigId = (state: RootState) => {
  return state.entities.recurringOffers.metadata.configurationId;
};

export const findSingleOffer = (state: RootState, id: string) => {
  const {
    entities: { offers: { byId: singleOffersById } = {} },
  } = state;

  return singleOffersById?.[id];
};

export const findRecurringOffer = (state: RootState, id: string) => {
  const {
    entities: { recurringOffers: { byId: recurringOffersById } = {} },
  } = state;

  return recurringOffersById?.[id];
};

const findOffer = (state: RootState, id: string) => {
  const {
    entities: { offers: { byId: singleOffersById } = {} },
  } = state;

  return singleOffersById?.[id];
};

const buildOfferWithEntities = <T extends Offer>(
  state: RootState,
  offer: T,
) => {
  const {
    entities: { prices },
  } = state;

  const price = prices[offer.price as string];
  const fee = getFee(offer, price);
  const fields = ((offer.fields as string[]) || NO_FIELDS).map(field =>
    getFieldById(state, field),
  );

  return { ...offer, price, fee, fields };
};
